import React from 'react';
import { CountryTemplate } from '../../components/CountryTemplate';

export const PeruPage = () => {
  const peruData = {
    name: 'Peru',
    region: 'Latin America',
    color: '#708080',
    metaTitle: 'Pharma & Nutraceutical Registration in Peru | DIGEMID | CTD & Supplements Rules',
    metaDescription: 'Explore how to register pharmaceutical and nutraceutical products in Peru. DIGEMID\'s requirements, CTD-aligned dossiers, food supplement classification, required documents and why Peru matters in Latin America for chronic therapies & wellness exports.',
    
    hero: {
      title: 'Pharma & Nutraceutical Registration in Peru',
      subtitle: 'A fast-evolving Latin-American market with growing imports, rising disease burden and regulatory frameworks moving toward CTD alignment - a strategic gateway for exporters targeting Latin America.',
      cta: 'Start Your Peru Registration'
    },
    
    atAGlance: [
      { label: 'Population', value: '~34-35 million' },
      { label: 'Regulator', value: 'DIGEMID under MINSA' },
      { label: 'Market Development', value: 'Growing pharma import dependence' },
      { label: 'Fast Track Law', value: 'Law 32319 (May 2025) - 45 days' },
      { label: 'Supplement Regulation', value: 'DIGESA (food) or DIGEMID (pharma format)' }
    ],
    
    marketOverview: {
      title: 'Market Overview: Growth with Reform',
      stats: [
        'Pharma market largely reliant on imported finished medicines',
        'DIGEMID continues to issue sanitary registrations for imported products',
        'Law No. 32319 (May 2025) offers expedited registration for medicines from high-surveillance countries',
        'Potentially down to 45 days for eligible products',
        'Two parallel authorities: DIGESA (food forms without health claims) and DIGEMID (pharmaceutical dosage forms or health claims)',
        'Regulatory environment improving with CTD alignment and digital tools'
      ],
      opportunities: [
        'Emerging market with regulatory reforms making entry more predictable',
        'Strategic Latin-American foothold for chronic-disease portfolios',
        'Growing import volumes with openness to high-quality foreign products',
        'Less saturated nutraceutical segment offering early-mover advantage'
      ]
    },
    
    diseaseburden: {
      title: 'Disease Profile & Wellness Trends',
      subtitle: 'Peru experiencing rising NCD burdens with government awareness of unmet therapeutic needs.',
      categories: [
        {
          name: 'Non-Communicable Diseases',
          items: [
            'Rising burden of diabetes, cardiovascular disease and obesity',
            'Law 32319 emphasizes orphan, rare and oncology products',
            'Government awareness of unmet therapeutic needs',
            'Pharma demand: Products for chronic conditions (diabetes, hypertension, lipids)',
            'Pharma demand: Specialty therapies (oncology, biologics)',
            'Generics continue to play important role',
            'Imported licensed medicines from high-surveillance countries have regulatory advantage'
          ]
        },
        {
          name: 'Wellness & Prevention Market',
          items: [
            'Growing uptake for wellness and lifestyle supplements',
            'Preventive care focus increasing',
            'Health-product regulation less mature than GCC - early mover advantage',
            'Less market saturation in supplements compared to mature markets',
            'Nutra opportunities: Weight-management, cardiovascular support, vitamins/minerals',
            'Must manage classification carefully (DIGESA vs DIGEMID pathway)',
            'Products aligned with preventive care trend finding traction'
          ]
        }
      ]
    },
    
    regulatoryFramework: {
      authority: 'DIGEMID - General Directorate of Medicines, Supplies & Drugs under MINSA',
      description: 'DIGEMID under MINSA regulates medicines, biologicals, health-products in pharmaceutical formats. DIGESA regulates food/dietary supplements without medicinal claims. Legal basis includes Law 29459 (Pharmaceutical Products, Medical Devices & Health Products) and Supreme Decree 002-2021-SA (conditional sanitary registration). Peru advancing towards CTD-structure dossiers and referencing High Surveillance Countries approvals.',
      pharma: {
        timeline: 'Official evaluation times vary by category and complexity. May take many months or years depending on category. Recent Law 32319 (May 2025) promises expedited review (~45 days) for eligible products from high surveillance countries. Registrations have validity periods requiring renewal.',
        requirements: [
          'Peruvian legal entity / local representative required to hold registration',
          'Dossier structure aligned to CTD modules (Modules 1-5) with local deviations for generics',
          'Application form and local agent documents',
          'GMP certificate (Peru or recognised High Surveillance Country) - not older than 2 years',
          'CPP / Free Sale Certificate from reference country (if required)',
          'Full product dossier: quality (CMC), non-clinical/clinical data',
          'Label/insert in Spanish, packaging information',
          'Correct classification under Peruvian regulations',
          'For biologicals: Comply with Supreme Decree 002-2021-SA guidelines',
          'Import licences/permissions required before market launch via VUCE portal'
        ],
        insight: 'Peru moving toward CTD alignment with local adaptations. Law 32319 (May 2025) offers fast-track (45 days) for products from high-surveillance countries (e.g., FDA, EMA approved). GMP certificates must be current (not older than 2 years). VUCE portal used for import procedures.'
      },
      nutra: {
        timeline: 'Depends on classification. Food pathway (DIGESA) generally faster than pharmaceutical pathway (DIGEMID). Clear classification determination critical to avoid delays.',
        requirements: [
          'Classification determination: food forms without health claims fall under DIGESA',
          'Dosage forms (tablets/capsules) or health claims move to DIGEMID regulation',
          'Define classification early: dosage form, claims, regulatory category determine path',
          'DIGEMID pathway: Full composition, COA, label in Spanish, local importer, sanitary registration',
          'DIGESA pathway: Import permit, composition, label compliance, VUCE import registration',
          'Adequate dossier varies by pathway but always includes Spanish labelling',
          'Local importer/agent registration required',
          'Compliance with Peruvian food-supplement rules',
          'VUCE portal (Single Window for Foreign Trade) for importation process'
        ],
        insight: 'Classification is critical decision point. Simple food supplements without health claims = DIGESA (food authority). Pharmaceutical dosage forms OR health claims = DIGEMID (pharmaceutical authority). Wrong classification causes significant delays. Must determine correct pathway early in planning.'
      }
    },
    
    pharmaPathway: {
      title: 'Pharmaceutical Registration with DIGEMID (CTD-Aligned)',
      steps: [
        'Local Agent / MAH - Peruvian legal entity / local representative required to hold registration',
        'Dossier Format - CTD-aligned structure (Modules 1-5) with local adaptations',
        'Formal application to DIGEMID',
        'Full CTD-aligned dossier with local Module 1 in Spanish',
        'Module 1: Peru-specific administrative/legal docs',
        'Modules 2-5: Quality (CMC), non-clinical/clinical data per standards',
        'GMP certificate for manufacturing site (Peru or recognised country) - must be current',
        'CPP / Free Sale Certificate from country of origin or reference authority',
        'Label/pack designs in Spanish',
        'Stability and analytical data',
        'Manufacturing/warehouse authorisations',
        'Local agent/MAH documentation',
        'Import licences/permissions via VUCE portal before market launch',
        'Assessment: Official times vary; can take months to year+',
        'Fast Track: Law 32319 provides ~45 day expedited review for high-surveillance country products',
        'Renewal & Variations per Decree 016-2011-SA and related regulations'
      ]
    },
    
    nutraPathway: {
      title: 'Nutraceuticals & Health Products in Peru',
      steps: [
        'Classification & Pathways: Products in food forms without health claims = DIGESA (food regulation)',
        'Products in dosage forms (tablets/capsules) OR with health claims = DIGEMID (pharmaceutical-style)',
        'Define classification early: dosage form, claims and regulatory category determine path',
        'DIGEMID Pathway (pharmaceutical format/health claims):',
        'Full composition documentation and COA',
        'Label in Spanish with compliant claims',
        'Local agent/importer registered with DIGEMID',
        'Manufacturing certificates and authorisations',
        'Sanitary registration application',
        'DIGESA Pathway (food format without health claims):',
        'Import permit application',
        'Composition and ingredient details',
        'Label compliance with food regulations',
        'VUCE import registration (Single Window for Foreign Trade)',
        'Ensure compliance with Peruvian food-supplement importation rules',
        'Local importer/distributor management'
      ]
    },
    
    requiredDocs: {
      'Pharmaceuticals (DIGEMID CTD)': [
        'Formal application to DIGEMID',
        'Full CTD-aligned dossier (Modules 1-5)',
        'Local Module 1 in Spanish with Peru-specific supplements',
        'GMP certificate (current - not older than 2 years)',
        'Free Sale Certificate / CPP from reference authority',
        'Label/pack designs in Spanish',
        'Stability and analytical data',
        'Manufacturing/warehouse authorisations',
        'Local agent/MAH documentation',
        'Import licences/permissions via VUCE'
      ],
      'Nutraceuticals / Health Products': [
        'Classification outcome documentation (food vs pharmaceutical)',
        'If DIGEMID regulated: Dossier with composition, COA, Spanish label, local agent registration, manufacturing certs, sanitary registration',
        'If DIGESA regulated: Import permit, composition details, label compliance, VUCE import registration',
        'Local importer/agent registered in Peru',
        'Compliance documentation with food/supplement regulations'
      ]
    },
    
    whyThisCountry: {
      title: 'Why Peru Should Be in Your LatAm Strategy',
      reasons: [
        'Emerging market with regulatory reforms (faster approvals, CTD alignment)',
        'Strategic Latin-American foothold for chronic-disease portfolios',
        'Growing import volumes with openness to high-quality foreign products from high-surveillance countries',
        'Less saturation in nutraceutical/health-product segment - early-mover advantage',
        'Law 32319 fast-track (45 days) for products from recognised countries',
        'Population of 34-35 million with rising healthcare needs',
        'Part of Pacific Alliance facilitating regional trade'
      ],
      bestPortfolio: [
        'Pharma: Antidiabetics, antihypertensives, lipid-lowering agents, chronic-care generics/innovators, biologics, specialty therapies (oncology), orphan/rare disease products',
        'Nutra: Metabolic-health supplements, cardiovascular support, premium vitamins/minerals, weight-management formulas aligned with preventive care trend'
      ]
    },
    
    howWeHelp: {
      title: 'How TheApostrophe Helps You Enter Peru',
      services: [
        'Portfolio & Market Fit Analysis - Align products with Peru\'s NCD trends, import gaps and regulatory opportunities',
        'CTD Dossier Adaptation - Convert to Peru-ready CTD package with Spanish localised Module 1',
        'GMP/CPP Verification - Ensure certificates meet Peru requirements (not older than 2 years)',
        'Local Agent Setup - Establish Peruvian legal entity or representative for registration holding',
        'Supplement Classification & Compliance - Identify DIGESA vs DIGEMID pathway, prepare appropriate documentation',
        'Import & Trade Compliance - Guide through VUCE portal for import/sanitary registration',
        'Label Compliance - Spanish language labelling meeting Peru requirements',
        'LatAm Cluster Advantage - Use Peru dossier as base for other LatAm registrations (Chile, Uruguay, Costa Rica)'
      ]
    },
    
    faqs: [
      {
        question: 'Who regulates medicines in Peru?',
        answer: 'The General Directorate of Medicines, Supplies & Drugs (DIGEMID) under the Ministry of Health (MINSA) regulates medicines, biologicals and health products in pharmaceutical formats.'
      },
      {
        question: 'Are dietary supplements regulated differently?',
        answer: 'Yes. If a product is a simple food or dietary product without health claims, it falls under DIGESA (food safety). If it\'s in pharmaceutical form or has health claims, it falls under DIGEMID (pharmaceutical regulation).'
      },
      {
        question: 'Does Peru require CTD format for medicines?',
        answer: 'Peru is advancing toward CTD alignment. Dossiers should follow CTD-aligned structure (Modules 1-5) though with some local adaptations, particularly for generic products.'
      },
      {
        question: 'How long does registration take?',
        answer: 'Practical timelines can stretch to many months, possibly up to a year or more depending on dossier complexity. However, Law 32319 (May 2025) provides expedited review (as low as ~45 days) for products from recognised high-surveillance countries.'
      },
      {
        question: 'What is Law 32319 and how does it help?',
        answer: 'Law 32319 (May 2025) provides expedited review (approximately 45 days) for products from recognised high-surveillance countries (e.g., FDA, EMA approved products), significantly reducing registration time for eligible medicines.'
      }
    ]
  };

  return <CountryTemplate countryData={peruData} />;
};
